import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import dayjs from "dayjs";

import useCampuses from "../Activities/hooks/useCampuses";
import useDailySummary, {
  formatBreakdown,
  formatOnSite,
  groupRoomsByHouse,
  formatRoomName,
  groupStaffByCategory,
  STAFF_QUALIFICATIONS,
  hasQual,
  staffChips,
} from "./hooks/useDailySummary";
import { exportDailySummaryPdf } from "./dailySummaryExport";
import "../AllTabs/InvoicePage.css";

const PUBLIC = process.env.PUBLIC_URL || "";
const LOGO = `${PUBLIC}/assets/img/blissLogo.png`;
const PLAYBOOK = `${PUBLIC}/assets/img/playbook.png`;

// A navy "summary-section" divider followed by a single line of text — used for
// the Arrivals/Departures counted breakdowns.
function SheetText({ title, count, text }) {
  return (
    <>
      <div className="summary-section">
        {title}
        {count != null ? ` (${count})` : ""}
      </div>
      <p style={{ margin: "6px 0 0", padding: "0 8px" }}>{text}</p>
    </>
  );
}

// One section inside the sheet: a navy "summary-section" divider + a table
// (or "None"), mirroring the Booking Summary preview.
function SheetSection({ title, count, columns, rows, renderRow }) {
  return (
    <>
      <div className="summary-section">
        {title}
        {count != null ? ` (${count})` : ""}
      </div>
      <table className="bookings-table">
        <tbody>
          {!rows || rows.length === 0 ? (
            <tr>
              <td style={{ opacity: 0.6 }}>None</td>
            </tr>
          ) : (
            <>
              <tr style={{ fontWeight: "bold" }}>
                {columns.map((c, i) => (
                  <td key={i}>{c}</td>
                ))}
              </tr>
              {rows.map((r, i) => (
                <tr key={i}>
                  {renderRow(r).map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </>
  );
}

// Staff section: a subtable per job category (Camp Manager, House Parents,
// Teachers, Activity Staff, Other) showing Name + a column per qualification
// (DSL / First Aid / Lifeguard), plus a qualification count line. Empty
// categories are omitted.
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < (arr || []).length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function renderStaff(s) {
  if (!s) return "";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
      <span>{s.name || ""}</span>
      {staffChips(s).map((c) => (
        <Chip key={c.code} size="small" label={c.label} variant="outlined" />
      ))}
    </span>
  );
}

// A bedroom list grouped by house: a house label then its rooms (left-aligned).
// Used for in-use / moving-in / being-vacated so they all look the same.
function BedroomList({ title, rooms, renderRoom }) {
  const houses = groupRoomsByHouse(rooms);
  return (
    <>
      <div className="summary-section">
        {title} ({(rooms || []).length})
      </div>
      {houses.length === 0 ? (
        <p style={{ margin: "6px 0 0", padding: "0 8px", opacity: 0.6, textAlign: "left" }}>
          None
        </p>
      ) : (
        houses.map((h) => (
          <div key={h.house} style={{ marginTop: 6, padding: "0 8px", textAlign: "left" }}>
            <div style={{ fontWeight: "bold", color: "#1A1F71" }}>{h.house}</div>
            <div>{h.rooms.map(renderRoom).join(", ")}</div>
          </div>
        ))
      )}
    </>
  );
}

const renderMoveRoom = (r) => `${r.name} (${r.count}/${r.capacity})`;

function StaffSection({ staff }) {
  const groups = groupStaffByCategory(staff);
  const countLine = STAFF_QUALIFICATIONS.map(
    (q) => `${q.countLabel}: ${staff.filter((s) => hasQual(s, q.code)).length}`
  ).join(" · ");

  return (
    <>
      <div className="summary-section">Staff working ({staff.length})</div>
      {staff.length > 0 && (
        <p style={{ margin: "6px 0 0", padding: "0 8px" }}>{countLine}</p>
      )}

      {groups.length === 0 ? (
        <p style={{ margin: "6px 0 0", padding: "0 8px", opacity: 0.6 }}>None</p>
      ) : (
        groups.map((g) => (
          <div key={g.key}>
            <div className="summary-section">{g.label}</div>
            <table className="bookings-table">
              <tbody>
                {chunk(g.members, 2).map((pair, ri) => (
                  <tr key={ri}>
                    <td style={{ width: "50%", verticalAlign: "top" }}>
                      {renderStaff(pair[0])}
                    </td>
                    <td style={{ width: "50%", verticalAlign: "top" }}>
                      {pair[1] ? renderStaff(pair[1]) : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </>
  );
}

function DaySheet({ campusName, day }) {
  return (
    <div
      className="summary-sheet"
      style={{
        background: "white",
        padding: 20,
        marginBottom: 20,
        border: "1px solid #ddd",
        borderRadius: 4,
        fontSize: 13,
        textAlign: "left",
      }}
    >
      <div id="report">
        <div className="images-container">
          <div
            className="logo-container"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <img alt="Bliss" src={LOGO} style={{ height: "70px" }} />
            <img
              alt="Daily Summary"
              src={PLAYBOOK}
              style={{ height: "45px", marginTop: "15px" }}
            />
          </div>
          <hr className="line" style={{ width: "100%" }} />
        </div>

        <div className="student-details-container">
          <div>
            <strong>{campusName}</strong>
          </div>
          <div>{dayjs(day.date).format("dddd D MMMM YYYY")}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <p>Classes running: {day.classCount}</p>
        </div>

        <div style={{ width: "100%", textAlign: "center" }}>
          <h2>Daily Summary</h2>
        </div>

        <SheetText title="On site" text={formatOnSite(day.onSite)} />

        <SheetText
          title="Arrivals"
          count={day.arrivals.total}
          text={formatBreakdown(day.arrivals)}
        />

        <SheetText
          title="Departures"
          count={day.departures.total}
          text={formatBreakdown(day.departures)}
        />

        <BedroomList title="Bedrooms in use" rooms={day.bedrooms.inUse} renderRoom={formatRoomName} />

        <BedroomList
          title="Moving in today"
          rooms={day.bedrooms.movingIn}
          renderRoom={renderMoveRoom}
        />

        <BedroomList
          title="Being vacated today"
          rooms={day.bedrooms.vacating}
          renderRoom={renderMoveRoom}
        />

        <SheetSection
          title="Allergies on site"
          count={day.allergies.length}
          columns={["First", "Last", "Allergies", "Type"]}
          rows={day.allergies}
          renderRow={(a) => [
            a.firstName || "",
            a.lastName || "",
            a.allergies || "",
            a.personType || "",
          ]}
        />

        <StaffSection staff={day.staff} />

        <SheetSection
          title="Classes"
          count={day.classes.length}
          columns={["Class", "Students", "Teacher"]}
          rows={day.classes}
          renderRow={(c) => [c.name || "", c.students, c.teacher || "—"]}
        />

        <SheetSection
          title="Activity groups"
          count={day.activityGroups.length}
          columns={["Group", "Type", "Students", "Adults", "Total", "Leader"]}
          rows={day.activityGroups}
          renderRow={(g) => [
            g.name || "",
            g.typeLabel,
            g.students,
            g.adults,
            g.total,
            g.leader || "Unassigned",
          ]}
        />
      </div>
    </div>
  );
}

export default function DailySummaryPage({ selectedDate, selectedCampusId }) {
  const { campuses = [] } = useCampuses();

  const campusList = useMemo(
    () => (campuses || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [campuses]
  );

  const [selectedCampus, setSelectedCampus] = useState("");
  const [startDate, setStartDate] = useState(selectedDate || dayjs());
  const [endDate, setEndDate] = useState(selectedDate || dayjs());
  const [rangeMode, setRangeMode] = useState(false);
  const [exporting, setExporting] = useState(false);
  const sheetsRef = useRef(null);

  const handleExportPdf = async () => {
    const nodes = sheetsRef.current?.querySelectorAll(".summary-sheet");
    if (!nodes || nodes.length === 0) return;
    setExporting(true);
    try {
      await exportDailySummaryPdf(nodes, selectedCampus);
    } finally {
      setExporting(false);
    }
  };

  // Seed the campus from the app-wide campus once campuses load.
  useEffect(() => {
    if (selectedCampus || campusList.length === 0) return;
    const match = campusList.find((c) => String(c.id) === String(selectedCampusId));
    if (match) setSelectedCampus(match.name);
  }, [campusList, selectedCampusId, selectedCampus]);

  const { days, loading, error, truncated, maxDays } = useDailySummary({
    campusName: selectedCampus,
    startDate,
    endDate,
  });

  const startStr = startDate ? dayjs(startDate).format("YYYY-MM-DD") : "";
  const endStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : "";
  const rangeValid = startStr && endStr && !dayjs(endStr).isBefore(dayjs(startStr), "day");
  const hasData = days.length > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Campus</InputLabel>
            <Select
              label="Campus"
              value={selectedCampus}
              onChange={(e) => setSelectedCampus(e.target.value)}
            >
              <MenuItem value="">
                <em>Select campus…</em>
              </MenuItem>
              {campusList.map((c) => (
                <MenuItem key={c.id} value={c.name}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {rangeMode ? (
            <>
              <TextField
                label="From"
                type="date"
                size="small"
                value={startStr}
                onChange={(e) => {
                  const d = dayjs(e.target.value);
                  if (d.isValid()) setStartDate(d);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={endStr}
                onChange={(e) => {
                  const d = dayjs(e.target.value);
                  if (d.isValid()) setEndDate(d);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </>
          ) : (
            <TextField
              label="Date"
              type="date"
              size="small"
              value={startStr}
              onChange={(e) => {
                const d = dayjs(e.target.value);
                if (d.isValid()) {
                  setStartDate(d);
                  setEndDate(d);
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          )}

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={rangeMode}
                onChange={(e) => {
                  const on = e.target.checked;
                  setRangeMode(on);
                  if (!on) setEndDate(startDate);
                }}
              />
            }
            label="Date range"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={!hasData || exporting}
          >
            {exporting ? "Exporting…" : "Export PDF"}
          </Button>
        </Stack>

        {!rangeValid && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Choose a valid date range (To on or after From).
          </Typography>
        )}
        {truncated && (
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            Range limited to the first {maxDays} days.
          </Typography>
        )}
      </Paper>

      {!selectedCampus ? (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Pick a campus and date range to build the summary.
        </Typography>
      ) : loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Building summary…</Typography>
        </Stack>
      ) : error ? (
        <Alert severity="error">{String(error?.message || error)}</Alert>
      ) : !hasData ? (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          No data for this selection.
        </Typography>
      ) : (
        <Box ref={sheetsRef} sx={{ maxWidth: 900 }}>
          {days.map((day) => (
            <DaySheet key={day.date} campusName={selectedCampus} day={day} />
          ))}
        </Box>
      )}
    </Box>
  );
}
